import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useVault } from "../../hooks/useVault";
import { useSession } from "../../stores/session";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { useQueryClient } from "@tanstack/react-query";
import { useUi } from "../../stores/ui";

export function SecurityTab() {
  const vault = useVault();
  const session = useSession((s) => s.session);
  const clearSession = useSession((s) => s.clear);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const autoLockMinutes = useUi((s) => s.autoLockMinutes);
  const setAutoLockMinutes = useUi((s) => s.setAutoLockMinutes);

  const [oldP, setOldP] = useState("");
  const [newP, setNewP] = useState("");
  const [newP2, setNewP2] = useState("");
  const [changing, setChanging] = useState(false);

  async function onChangePass(): Promise<void> {
    if (!session) return;
    if (newP !== newP2) {
      toast.error("New passphrase does not match confirmation");
      return;
    }
    setChanging(true);
    const a = toPassphraseBytes(oldP);
    const b = toPassphraseBytes(newP);
    try {
      await vault.changePassphrase(session.handle, a, b);
      toast.success("Passphrase changed");
      setOldP("");
      setNewP("");
      setNewP2("");
    } catch (err) {
      toast.error(`Change failed: ${String(err)}`);
    } finally {
      zeroize(a);
      zeroize(b);
      setChanging(false);
    }
  }

  async function onLockNow(): Promise<void> {
    if (!session) return;
    await vault.lockVault(session.handle);
    qc.clear();
    clearSession();
    navigate("/master");
  }

  async function onRunGc(): Promise<void> {
    if (!session) return;
    try {
      const report = await vault.runGc(session.handle);
      toast.success(
        `GC reclaimed ${report.immutable_deleted + report.mutable_deleted} chunks`,
      );
      void qc.invalidateQueries({ queryKey: ["gc-stats", session.handle] });
    } catch (err) {
      toast.error(`GC failed: ${String(err)}`);
    }
  }

  return (
    <div className="max-w-xl space-y-8">
      <section className="space-y-2">
        <h2 className="text-xl font-medium">Passphrase</h2>
        <p className="max-w-[50ch] text-xs text-text-soft">
          Change the passphrase that unlocks this vault. We re-encrypt the
          keybag with a fresh Argon2id salt and rewrite the manifest
          atomically. Your files are untouched.
        </p>
        <label className="mt-3 block text-[11px] uppercase tracking-[0.1em] text-text-dim">
          Old passphrase
        </label>
        <PassphraseInput
          autoCompleteMode="current-password"
          value={oldP}
          onChange={(e) => setOldP(e.target.value)}
        />
        <label className="mt-3 block text-[11px] uppercase tracking-[0.1em] text-text-dim">
          New passphrase
        </label>
        <PassphraseInput
          autoCompleteMode="new-password"
          value={newP}
          onChange={(e) => setNewP(e.target.value)}
        />
        <label className="mt-3 block text-[11px] uppercase tracking-[0.1em] text-text-dim">
          Confirm new passphrase
        </label>
        <PassphraseInput
          autoCompleteMode="new-password"
          value={newP2}
          onChange={(e) => setNewP2(e.target.value)}
        />
        <button
          onClick={onChangePass}
          disabled={changing || oldP.length === 0 || newP.length < 8}
          className="mt-3 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          {changing ? "Changing…" : "Change passphrase"}
        </button>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-medium">Session</h2>
        <div className="flex items-center justify-between border-t border-border py-3 text-sm">
          <span>Auto-lock after inactivity</span>
          <select
            value={autoLockMinutes}
            onChange={(e) => setAutoLockMinutes(Number(e.target.value))}
            className="rounded-md border border-border bg-white/5 px-3 py-1 text-sm"
          >
            <option value={1}>1 minute</option>
            <option value={5}>5 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={60}>1 hour</option>
            <option value={0}>Never (not recommended)</option>
          </select>
        </div>
        <div className="flex items-center justify-between border-t border-border py-3 text-sm">
          <span>Lock now</span>
          <button
            onClick={onLockNow}
            className="rounded-md border border-border px-3 py-1.5 text-xs text-text-soft"
          >
            Lock vault
          </button>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-xl font-medium">Storage maintenance</h2>
        <p className="max-w-[50ch] text-xs text-text-soft">
          Removed files, overwritten chunks, or crash-interrupted uploads can
          leave orphan chunks behind. GC walks reachability from the volume
          descriptor and deletes anything unreferenced.
        </p>
        <div className="flex items-center justify-between border-t border-border py-3 text-sm">
          <span>Run garbage collection</span>
          <button
            onClick={onRunGc}
            className="rounded-md border border-border px-3 py-1.5 text-xs text-text-soft"
          >
            Run GC
          </button>
        </div>
      </section>
    </div>
  );
}
